'use client';

import { runtimeConfig } from '@/config/runtime-config';
import { Button } from '@/components/ui/button';
import { useMediaQuerySafe } from '@/hooks/useMediaQuerySafe';
import Image from 'next/image';
import { useCallback, useEffect, useRef, useState } from 'react';
import { IconCheck } from '@tabler/icons-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useTranslations } from 'next-intl';
import { copyToClipboard } from '@/lib/notify';

type Props = {
  url: string;
  title: string;
  size?: number;
  copiedText?: string;
  copyOnly?: boolean;
  label?: string;
};

export const ShareButton = ({ url, title, size = 36, copiedText, copyOnly = false, label }: Props) => {
  const isMobile = useMediaQuerySafe('(max-width: 1023px)');
  const t = useTranslations('product');
  const toastT = useTranslations('toasts');
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const successText = copiedText ?? t('url_copied');

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const getFullUrl = useCallback(() => {
    if (typeof window === 'undefined') return url;

    return new URL(url, window.location.origin).toString();
  }, [url]);

  const copyText = useCallback(async () => {
    const success = await copyToClipboard(getFullUrl(), { error: toastT('copy_error') });
    if (!success) return;

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setCopied(true);
    timeoutRef.current = setTimeout(() => setCopied(false), 2000);
  }, [getFullUrl, toastT]);

  const shareUrl = useCallback(async () => {
    if (typeof navigator === 'undefined') return;

    const fullUrl = getFullUrl();

    if (runtimeConfig.isTelegramMiniApp) {
      const openTelegramLink = window.Telegram?.WebApp?.openTelegramLink;

      if (openTelegramLink) {
        const telegramShareUrl = new URL('https://t.me/share/url');
        telegramShareUrl.searchParams.set('url', fullUrl);
        telegramShareUrl.searchParams.set('text', title);
        openTelegramLink(telegramShareUrl.toString());
        return;
      }
    }

    if (navigator.share) {
      try {
        await navigator.share({ url: fullUrl, title });
        return;
      } catch (error: any) {
        console.error('error', error);
        if (error instanceof DOMException && error.name === 'AbortError') {
          return;
        }
      }
    }
    await copyText();
  }, [getFullUrl, title, copyText]);

  return (
    <TooltipProvider>
      <Tooltip open={copied}>
        <TooltipTrigger asChild>
          <Button
            type='button'
            variant='ghost'
            size='icon'
            onClick={copyOnly || !isMobile ? copyText : shareUrl}
            aria-label={copied ? successText : (label ?? t('share'))}
            className='size-11 bg-transparent text-main-green hover:bg-transparent hover:text-main-green transition-transform duration-150 active:scale-90 motion-reduce:transition-none motion-reduce:active:scale-100'
          >
            {copied ? (
              <IconCheck
                className='size-auto'
                style={{ width: (size * 2) / 3, height: (size * 2) / 3 }}
                aria-hidden='true'
              />
            ) : (
              <Image src='/assets/icons/share-icon.svg' height={size} width={size} alt='' />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent side='top' align='end' sideOffset={6}>
          {successText}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
